import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { PortalClient, OtpRequestResult } from '@mys/contracts';
import { api, ApiError, forgetCsrfToken, setUnauthorizedListener } from './api.js';

/* ============================================================================
   Which client is logged in.
   ----------------------------------------------------------------------------
   The session lives in an httpOnly cookie, which JavaScript cannot read by
   design, so the app cannot know whether it is logged in without asking.
   `GET /api/auth/portal/me` on load is not an optimisation to remove later; it
   is the only way to answer the question after a refresh.

   `status` is four-valued rather than a boolean, and each value is a different
   ANSWER to "are we logged in?":

     loading       we have not asked yet
     authenticated yes
     anonymous     no — the API said 401
     unavailable   we could not ask

   Collapsing the first into the third makes the app flash its login screen on
   every reload. Collapsing the LAST into it is worse, and it shipped: the
   bootstrap caught every error and called it `anonymous`, so one dropped
   request threw away a good session and asked for a fresh WhatsApp code.

   That costs more here than in the admin. This audience is a shop owner on a
   mid-range Android over 4G — dropped requests are the normal case, not the
   edge — and getting back in is not retyping a password but waiting for an
   OTP, capped at five an hour. See CONTEXT.md §15.
   ========================================================================== */

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'unavailable';

interface SessionValue {
  status: SessionStatus;
  client: PortalClient | null;
  /** Ask for a code. Resolves with the server's own countdown figures. */
  requestCode: (phone: string) => Promise<OtpRequestResult>;
  /** Exchange a code for a session. */
  verifyCode: (phone: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Called when any request 401s, so one dead session logs the whole app out. */
  expire: () => void;
  /** Ask again after an `unavailable`. */
  retry: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

interface MeResponse {
  ok: true;
  client: PortalClient;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [client, setClient] = useState<PortalClient | null>(null);

  /** Bumped by `retry`, which re-runs the effect below. */
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setStatus('loading');
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    api
      .get<MeResponse>('/api/auth/portal/me', controller.signal)
      .then((response) => {
        setClient(response.client);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setClient(null);
        /* Only a 401 means "not logged in". Anything else — 429, 5xx, the
           request never landing — means we could not ask, and sending this
           customer back to the code screen for that is how a working session
           turns into a wait for an OTP they do not need. */
        setStatus(error instanceof ApiError && error.isUnauthorized ? 'anonymous' : 'unavailable');
      });

    return () => controller.abort();
  }, [attempt]);

  const requestCode = useCallback(
    (phone: string) => api.post<OtpRequestResult>('/api/auth/otp/request', { phone }),
    [],
  );

  const verifyCode = useCallback(async (phone: string, code: string) => {
    const response = await api.post<MeResponse>('/api/auth/otp/verify', { phone, code });
    setClient(response.client);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/portal/logout');
    } catch (error) {
      /* Clearing an httpOnly cookie needs the server, so a failure here means
         the cookie may survive. Still drop the local state: staying "logged
         in" on screen after someone asked to leave is the worse outcome, and
         the next request will 401 anyway. */
      if (!(error instanceof ApiError)) throw error;
    }
    forgetCsrfToken();
    setClient(null);
    setStatus('anonymous');
  }, []);

  const expire = useCallback(() => {
    forgetCsrfToken();
    setClient(null);
    setStatus('anonymous');
  }, []);

  /* One dead session logs the whole app out, wherever the 401 surfaced. */
  useEffect(() => setUnauthorizedListener(expire), [expire]);

  const value = useMemo<SessionValue>(
    () => ({ status, client, requestCode, verifyCode, logout, expire, retry }),
    [status, client, requestCode, verifyCode, logout, expire, retry],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
